
import { useState, useRef, useEffect } from 'react';

const DEFAULT_DRINKS = [
  { name: 'Beer', emoji: '🍺', color: '#F59E0B' },
  { name: 'Wine', emoji: '🍷', color: '#DC2626' },
  { name: 'Cocktail', emoji: '🍹', color: '#EC4899' },
  { name: 'Whiskey', emoji: '🥃', color: '#92400E' },
  { name: 'Margarita', emoji: '🍸', color: '#10B981' },
  { name: 'Champagne', emoji: '🥂', color: '#FBBF24' },
  { name: 'Sake', emoji: '🍶', color: '#E5E7EB' },
  { name: 'Shot', emoji: '🥃', color: '#7C3AED' },
];

const DRINK_EMOJIS = ['🍺', '🍷', '🍹', '🥃', '🍸', '🥂', '🍶', '🧃', '🥤', '🍵', '☕', '🧋', '🍾', '🫗', '🍻'];
const DRINK_COLORS = ['#F59E0B', '#DC2626', '#EC4899', '#92400E', '#10B981', '#FBBF24', '#7C3AED', '#0EA5E9', '#F97316', '#8B5CF6', '#14B8A6', '#EF4444', '#6366F1', '#84CC16', '#F43F5E'];

interface Drink {
  name: string;
  emoji: string;
  color: string;
}

interface DrinkPreset {
  name: string;
  drinks: Drink[];
}

interface DrinkWheelProps {
  show: boolean;
  winner: string;
  onResult: (drink: string, emoji: string) => void;
  onBack: () => void;
  onClose: () => void;
}

export default function DrinkWheel({ show, winner, onResult, onBack, onClose }: DrinkWheelProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [drinks, setDrinks] = useState<Drink[]>(() => {
    const saved = localStorage.getItem('customDrinks');
    return saved ? JSON.parse(saved) : DEFAULT_DRINKS;
  });
  const [newDrinkName, setNewDrinkName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [savedPresets, setSavedPresets] = useState<DrinkPreset[]>(() => {
    const saved = localStorage.getItem('drinkPresets');
    return saved ? JSON.parse(saved) : [];
  });
  const wheelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const presetInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setRotation(0);
      setShowAddForm(false);
      setShowSaveModal(false);
      setShowLoadModal(false);
    } else {
      const t = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(t);
    }
  }, [show]);

  useEffect(() => {
    localStorage.setItem('customDrinks', JSON.stringify(drinks));
  }, [drinks]);

  useEffect(() => {
    localStorage.setItem('drinkPresets', JSON.stringify(savedPresets));
  }, [savedPresets]);

  useEffect(() => {
    if (showAddForm && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showAddForm]);

  useEffect(() => {
    if (showSaveModal && presetInputRef.current) {
      presetInputRef.current.focus();
    }
  }, [showSaveModal]);

  const addDrink = () => {
    const trimmedName = newDrinkName.trim();
    if (!trimmedName || drinks.some(d => d.name.toLowerCase() === trimmedName.toLowerCase())) {
      return;
    }
    
    const randomEmoji = DRINK_EMOJIS[Math.floor(Math.random() * DRINK_EMOJIS.length)];
    const randomColor = DRINK_COLORS[Math.floor(Math.random() * DRINK_COLORS.length)];
    
    setDrinks([...drinks, { name: trimmedName, emoji: randomEmoji, color: randomColor }]);
    setNewDrinkName('');
    setShowAddForm(false);
  };

  const removeDrink = (name: string) => {
    if (drinks.length <= 2) return;
    setDrinks(drinks.filter(d => d.name !== name));
  };

  const resetToDefault = () => {
    setDrinks(DEFAULT_DRINKS);
  };

  const savePreset = () => {
    const trimmedName = presetName.trim();
    if (!trimmedName || drinks.length < 2) return;
    
    const existingIndex = savedPresets.findIndex(p => p.name.toLowerCase() === trimmedName.toLowerCase());
    if (existingIndex >= 0) {
      const updated = [...savedPresets];
      updated[existingIndex] = { name: trimmedName, drinks: [...drinks] };
      setSavedPresets(updated);
    } else {
      setSavedPresets([...savedPresets, { name: trimmedName, drinks: [...drinks] }]);
    }
    setPresetName('');
    setShowSaveModal(false);
  };

  const loadPreset = (preset: DrinkPreset) => {
    setDrinks([...preset.drinks]);
    setShowLoadModal(false);
  };

  const deletePreset = (name: string) => {
    setSavedPresets(savedPresets.filter(p => p.name !== name));
  };

  const spinWheel = () => {
    if (isSpinning || drinks.length < 2) return;

    setIsSpinning(true);
    const spins = 5 + Math.random() * 3;
    const extraDegrees = Math.random() * 360;
    const totalRotation = rotation + spins * 360 + extraDegrees;

    setRotation(totalRotation);

    setTimeout(() => {
      const normalizedRotation = totalRotation % 360;
      const segmentAngle = 360 / drinks.length;
      
      // The pointer is at the top (90 degrees), so we need to adjust for that
      // We subtract 90 to align with the top pointer position
      const adjustedRotation = (normalizedRotation + 90) % 360;
      
      // Calculate which segment the pointer is pointing at
      const winningIndex = Math.floor(adjustedRotation / segmentAngle) % drinks.length;
      const selectedDrink = drinks[winningIndex];

      setIsSpinning(false);
      onResult(selectedDrink.name, selectedDrink.emoji);
    }, 4000);
  };

  if (!isVisible) return null;

  const segmentAngle = 360 / drinks.length;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        show ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!isSpinning ? onClose : undefined}></div>

      <div
        className={`relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 transform transition-all duration-300 max-h-[90vh] overflow-y-auto ${
          show ? 'scale-100' : 'scale-90'
        }`}
      >
        <button
          onClick={!isSpinning ? onClose : undefined}
          className={`absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors ${isSpinning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="text-center mb-4">
          <h2 className="text-xl font-extrabold text-gray-900">What Drink?</h2>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-semibold text-[#2349FF]">{winner}</span> is buying! Spin to pick the drink!
          </p>
        </div>

        {/* Preset Actions */}
        <div className="flex justify-center gap-2 mb-4">
          <button
            onClick={() => setShowSaveModal(true)}
            disabled={isSpinning || drinks.length < 2}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
              isSpinning || drinks.length < 2
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 cursor-pointer'
            }`}
          >
            <i className="ri-save-line mr-1"></i>Save
          </button>
          <button
            onClick={() => setShowLoadModal(true)}
            disabled={isSpinning || savedPresets.length === 0}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
              isSpinning || savedPresets.length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-amber-50 text-amber-600 hover:bg-amber-100 cursor-pointer'
            }`}
          >
            <i className="ri-folder-open-line mr-1"></i>Load
          </button>
        </div>

        {/* Save Modal */}
        {showSaveModal && (
          <div className="mb-4 p-3 bg-emerald-50 rounded-xl">
            <p className="text-xs font-medium text-emerald-700 mb-2">Save current drinks as preset:</p>
            <div className="flex gap-2">
              <input
                ref={presetInputRef}
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && savePreset()}
                placeholder="Preset name..."
                maxLength={30}
                className="flex-1 px-3 py-2 text-sm border border-emerald-200 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
              />
              <button
                onClick={savePreset}
                disabled={!presetName.trim()}
                className="px-4 py-2 bg-emerald-500 text-white text-sm font-semibold rounded-full hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setPresetName('');
                }}
                className="px-3 py-2 bg-white text-gray-600 text-sm rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* Load Modal */}
        {showLoadModal && (
          <div className="mb-4 p-3 bg-amber-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-amber-700">Load a saved preset:</p>
              <button
                onClick={() => setShowLoadModal(false)}
                className="w-6 h-6 flex items-center justify-center text-amber-600 hover:bg-amber-100 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {savedPresets.map((preset) => (
                <div
                  key={preset.name}
                  className="group flex items-center justify-between p-2 bg-white rounded-lg hover:bg-amber-100 transition-colors cursor-pointer"
                  onClick={() => loadPreset(preset)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{preset.drinks[0]?.emoji}</span>
                    <span className="text-sm font-medium text-gray-700">{preset.name}</span>
                    <span className="text-xs text-gray-400">({preset.drinks.length} drinks)</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deletePreset(preset.name);
                    }}
                    className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Wheel Container */}
        <div className="relative w-64 h-64 mx-auto mb-5">
          {/* Pointer */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 z-10">
            <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-[#2349FF] drop-shadow-lg"></div>
          </div>

          {/* Wheel */}
          <div
            ref={wheelRef}
            className="w-full h-full rounded-full shadow-xl overflow-hidden border-4 border-white"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {drinks.map((drink, index) => {
                const startAngle = index * segmentAngle - 90;
                const endAngle = startAngle + segmentAngle;
                const startRad = (startAngle * Math.PI) / 180;
                const endRad = (endAngle * Math.PI) / 180;
                const x1 = 50 + 50 * Math.cos(startRad);
                const y1 = 50 + 50 * Math.sin(startRad);
                const x2 = 50 + 50 * Math.cos(endRad);
                const y2 = 50 + 50 * Math.sin(endRad);
                const largeArc = segmentAngle > 180 ? 1 : 0;

                const midAngle = startAngle + segmentAngle / 2;
                const midRad = (midAngle * Math.PI) / 180;
                const textX = 50 + 32 * Math.cos(midRad);
                const textY = 50 + 32 * Math.sin(midRad);

                return (
                  <g key={drink.name}>
                    <path
                      d={`M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArc} 1 ${x2} ${y2} Z`}
                      fill={drink.color}
                      stroke="white"
                      strokeWidth="0.5"
                    />
                    <text
                      x={textX}
                      y={textY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={drinks.length > 12 ? '8' : drinks.length > 8 ? '10' : '12'}
                      transform={`rotate(${midAngle + 90}, ${textX}, ${textY})`}
                    >
                      {drink.emoji}
                    </text>
                  </g>
                );
              })}
              <circle cx="50" cy="50" r="8" fill="white" stroke="#e5e7eb" strokeWidth="1" />
            </svg>
          </div>
        </div>

        {/* Drink Labels with Remove Option */}
        <div className="flex flex-wrap justify-center gap-1.5 mb-4">
          {drinks.map((drink) => (
            <div
              key={drink.name}
              className="group flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-full text-xs hover:bg-gray-100 transition-colors"
            >
              <span>{drink.emoji}</span>
              <span className="text-gray-600">{drink.name}</span>
              {drinks.length > 2 && !isSpinning && (
                <button
                  onClick={() => removeDrink(drink.name)}
                  className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Add Custom Drink */}
        <div className="mb-5">
          {showAddForm ? (
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newDrinkName}
                onChange={(e) => setNewDrinkName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addDrink()}
                placeholder="Enter drink name..."
                maxLength={20}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-[#2349FF]/20 focus:border-[#2349FF]"
              />
              <button
                onClick={addDrink}
                disabled={!newDrinkName.trim()}
                className="px-4 py-2 bg-[#2349FF] text-white text-sm font-semibold rounded-full hover:bg-[#1a38cc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewDrinkName('');
                }}
                className="px-3 py-2 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          ) : (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setShowAddForm(true)}
                disabled={isSpinning}
                className={`px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                  isSpinning ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 cursor-pointer'
                }`}
              >
                <i className="ri-add-line mr-1"></i>Add Drink
              </button>
              {JSON.stringify(drinks) !== JSON.stringify(DEFAULT_DRINKS) && (
                <button
                  onClick={resetToDefault}
                  disabled={isSpinning}
                  className={`px-4 py-2 bg-gray-100 text-gray-500 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                    isSpinning ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 cursor-pointer'
                  }`}
                >
                  <i className="ri-refresh-line mr-1"></i>Reset
                </button>
              )}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-2.5">
          <button
            onClick={onBack}
            disabled={isSpinning}
            className={`flex-1 px-5 py-3 bg-gray-100 text-gray-600 text-sm font-bold rounded-full transition-colors whitespace-nowrap ${
              isSpinning ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 cursor-pointer'
            }`}
          >
            <i className="ri-arrow-left-line mr-1.5"></i>Back
          </button>
          <button
            onClick={spinWheel}
            disabled={isSpinning || drinks.length < 2}
            className={`flex-1 px-5 py-3 bg-gradient-to-r from-[#2349FF] to-[#1a38cc] text-white text-sm font-bold rounded-full transition-all whitespace-nowrap ${
              isSpinning || drinks.length < 2 ? 'opacity-70 cursor-not-allowed' : 'hover:from-[#1a38cc] hover:to-[#1530b3] cursor-pointer'
            }`}
          >
            {isSpinning ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Spinning...
              </>
            ) : (
              <>
                <i className="ri-refresh-line mr-1.5"></i>Spin for Drink!
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
